/**
 * Viewport-Unbound Telemetry · T7127.
 *
 * Detects hooks that mounted with `realtime.viewportGated = true` but
 * never bound to an element (the engineer forgot to attach the callback
 * ref). Symptom in production: the hook never enters the registry's
 * `acquireWhenVisible` path, so no per-entity SUB is opened — cards
 * appear blank or "stuck stale" until the user navigates away and back.
 *
 * Production threshold is 30 s after mount. Dev-mode uses a 5 s window
 * with a `console.warn` (wired separately in `useRealtimeQuery`).
 *
 * Emission is rate-limited to one event per spec per session via
 * sessionStorage (see acceptance #2).
 */

/** Production delay before the never-bound emit fires. */
export const VIEWPORT_UNBOUND_PROD_DELAY_MS = 30_000;

/** sessionStorage key namespace — keep prefix short for quota friendliness. */
export const VIEWPORT_UNBOUND_STORAGE_PREFIX = 'morfi-vp-unbound:';

export interface ViewportUnboundTelemetry {
  /**
   * Arms a never-bound timer. Returns a cancel fn — call when the hook
   * DOES bind to an element (or on unmount before the threshold).
   */
  armNeverBoundTimer(specName: string): () => void;
}

/**
 * Factory. `getStorage` is a thunk because sessionStorage may not exist
 * at module-eval time (SSR, RN); resolving lazily avoids a hard reference.
 */
export function makeViewportUnboundTelemetry(
  emit: (event: 'sdk.viewport_unbound', payload: object) => void,
  getStorage: () => Pick<Storage, 'getItem' | 'setItem'> | null,
  opts: { delayMs?: number } = {},
): ViewportUnboundTelemetry {
  const delayMs = opts.delayMs ?? VIEWPORT_UNBOUND_PROD_DELAY_MS;

  function armNeverBoundTimer(specName: string): () => void {
    const handle = setTimeout(() => {
      const storage = getStorage();
      const key = `${VIEWPORT_UNBOUND_STORAGE_PREFIX}${specName}`;
      // Session-scoped dedup: storage may be unavailable (RN, locked
      // private mode). When it is, we still emit — losing the dedup is
      // preferable to losing the alert.
      if (storage !== null) {
        try {
          if (storage.getItem(key)) return;
          storage.setItem(key, '1');
        } catch {
          // Quota or privacy-mode rejection; fall through and emit.
        }
      }
      emit('sdk.viewport_unbound', { spec: specName });
    }, delayMs);
    return (): void => clearTimeout(handle);
  }

  return { armNeverBoundTimer };
}
